// Firmar MUCHOS objetos del bucket en UN pedido —
// ADR-2026-09-08-las-creaciones-del-panel-se-firman-en-lote.
//
// Nació dentro de `useCreacionesRecientes` y salió acá cuando la columna de resultados
// necesitó lo mismo para pre-firmar los originales de las cards visibles
// (ADR-2026-09-08-el-visor-abre-con-la-fila-que-el-panel-ya-tenia).

import { supabase } from '../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from './imageCache';

/** Cuánto dura una URL firmada, en segundos. El mismo TTL que usa el historial. */
export const TTL_FIRMA = 3600;

/**
 * Devuelve un mapa `path → URL firmada`.
 *
 * Firmar de a una (`createSignedUrl` por objeto) cuesta una request de Storage por path:
 * para las ~19 filas de la columna de resultados eran ~5 vueltas al servidor antes de
 * poder dibujar nada. Acá va una sola.
 *
 * Los paths que no se pudieron firmar —el objeto no existe— simplemente NO están en el
 * mapa: es así como quien llama detecta que tiene que probar otra cosa (por ejemplo caer
 * del `opt/` al `full/`). Lo que ya está en la caché de firmas ni se pide, así que con la
 * caché caliente la función no sale a la red.
 */
export async function firmarEnLote(
  paths: string[],
  bucket = 'generadas',
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const pendientes: string[] = [];
  const yaPedido = new Set<string>();
  for (const path of paths) {
    const cacheada = getCachedSignedUrl(path);
    if (cacheada) out.set(path, cacheada);
    else if (!yaPedido.has(path)) {
      yaPedido.add(path);
      pendientes.push(path);
    }
  }
  if (pendientes.length === 0) return out;

  const { data } = await supabase.storage
    .from(bucket)
    .createSignedUrls(pendientes, TTL_FIRMA);
  (data ?? []).forEach((item, i) => {
    // Por `path` y NO por índice: `createSignedUrls` no promete devolver las firmas en el
    // mismo orden en que se las pidió (el mismo cuidado que documenta `ordenarCapas` en
    // edición gráfica, donde el orden equivocado daba vuelta el z-order de las capas).
    // El índice queda de respaldo por si un item viniera sin `path`.
    const path = item.path ?? pendientes[i];
    if (!path || !item.signedUrl) return;
    out.set(path, item.signedUrl);
    setCachedSignedUrl(path, item.signedUrl);
  });
  return out;
}

export default firmarEnLote;
